import { ClipboardCheck } from '../../../../icons';
import { cn } from '../../../../lib/utils';
import { TruncatedText } from '../../TruncatedText';
import { coverageTone, type ScriptReviewResult } from '../types';

type Row = NonNullable<ScriptReviewResult['brief_coverage']>[number];

const STATUS_RANK: Record<Row['status'], number> = {
  missing: 0,
  partial: 1,
  met: 2,
};

export function BriefCoverageTable({ rows }: { rows: Row[] }) {
  if (!rows || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]);
  const counts = sorted.reduce<Record<Row['status'], number>>(
    (acc, r) => {
      acc[r.status] += 1;
      return acc;
    },
    { met: 0, partial: 0, missing: 0 },
  );

  return (
    <section className="mx-4 mt-4 rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={14} className="text-violet-500" />
          <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
            Brief coverage · {sorted.length}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px]">
          <CountChip count={counts.met} status="met" />
          <CountChip count={counts.partial} status="partial" />
          <CountChip count={counts.missing} status="missing" />
        </div>
      </div>
      <ul className="divide-y divide-border">
        {sorted.map((row, i) => (
          <CoverageRow key={`${row.requirement_id}-${i}`} row={row} />
        ))}
      </ul>
    </section>
  );
}

function CountChip({ count, status }: { count: number; status: Row['status'] }) {
  const tone = coverageTone(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-semibold tabular-nums',
        tone.bg,
        tone.text,
        tone.border,
      )}
    >
      <span className="tabular-nums">{count}</span>
      <span className="tracking-wide uppercase">{status}</span>
    </span>
  );
}

function CoverageRow({ row }: { row: Row }) {
  const tone = coverageTone(row.status);
  return (
    <li className="grid grid-cols-[7rem_1fr_4.5rem] items-start gap-3 px-4 py-2.5 text-caption-regular">
      <div className="min-w-0">
        <TruncatedText
          as="p"
          text={row.requirement_id}
          className="font-mono text-[10px] font-semibold tracking-tight text-muted-foreground"
        />
        <TruncatedText
          as="p"
          lines={2}
          text={row.requirement_text}
          className="mt-0.5 text-[11px] font-medium text-foreground"
        />
      </div>
      <TruncatedText
        as="p"
        lines={2}
        text={row.evidence ?? ''}
        className="text-[11px] leading-relaxed text-muted-foreground italic"
      />
      <span
        className={cn(
          'justify-self-end rounded-full border px-2 py-0.5 text-[9px] font-bold tracking-widest uppercase',
          tone.bg,
          tone.text,
          tone.border,
        )}
      >
        {row.status}
      </span>
    </li>
  );
}
